import { useCallback } from 'react';

import { useBridgeApi } from '@/api/BridgeApi';
import {
	asReasonList,
	renderLongReads,
} from '@/spaces/content/inspirationFormatting';
import { trackEvent } from '@/utils/fullstory';

import type { InspirationReference } from '../../../../bridge/types/artifact.ts';

// Matches the cap the linked-item prompt block applies to an artifact's content
// (services/py/chat/context_curator.py), so this context does not grow the prompt.
const ANALYSIS_CHAR_CAP = 4000;

/** The row title for a post the daily job found.
 *
 * The business leads, because a post from someone else must never read as the
 * customer's own work. Two surfaces show these rows — the home feed and the
 * job's panel — and they must agree, so the rule lives here beside the press
 * handler both of them already use.
 */
export const jobFindTitle = (artifact: InspirationReference): string => {
	const business = artifact.content.found_by_job_for?.trim();
	const topic = artifact.content.topic?.trim();
	return topic ? `${business}: ${topic}` : `${business}`;
};

/** The read the job already did, handed to the agent so it can talk about the
 * post rather than re-derive it. The customer never sees this text. */
export const buildJobContext = (artifact: InspirationReference): string => {
	const {
		topic,
		source_url,
		content_analysis,
		imagery_analysis,
		hook,
		pacing,
		payoff,
		suggestion,
		learning,
		learning_theme,
		format_name,
		why_it_works,
		found_by_job_for,
	} = artifact.content;

	const header = [
		`Bridgeway found this post while studying ${found_by_job_for || 'a similar business'}.`,
		'The customer did not save it. It is a find, and this conversation is about',
		'why it works and what the customer could take from it.',
		`Business: ${found_by_job_for || 'a similar business'}`,
		`Topic: ${topic || 'Untitled'}`,
		`Source: ${source_url}`,
		'',
	].join('\n');

	// The short reads get their room FIRST, and the two long descriptive reads
	// then take only what is left. Ordering them ahead of these and trusting the
	// tail cut was not enough: `imagery_analysis` and `content_analysis` are the
	// only unbounded fields here, so either one can spend the whole budget and
	// push Hook, Pacing, Payoff and What to borrow past the cut — the four the
	// conversation is actually about. Same reserved-room shape as
	// useInspirationChat.ts, which carries one long read and already does this.
	const renderSections = (
		pairs: ReadonlyArray<readonly [string, string | undefined]>,
	): string =>
		pairs
			.filter(([, body]) => body?.trim())
			.map(([label, body]) => `${label}: ${body}`)
			.join('\n\n');

	const shortSections = renderSections([
		['The lesson', learning],
		['Theme', learning_theme],
		['Repeatable format', format_name],
		['Why it works', asReasonList(why_it_works).join('; ')],
		['Hook', hook],
		['Pacing', pacing],
		['Payoff', payoff],
		['What to borrow', suggestion],
	]);

	const shortBlock = shortSections ? `${shortSections}\n\n` : '';
	const room = ANALYSIS_CHAR_CAP - header.length - shortBlock.length;
	// Each long read gets its own share. Cutting the tail of both together let a
	// long `imagery_analysis` spend the whole budget and delete `What it says`.
	const longSections = renderLongReads(
		[
			['What it looks like', imagery_analysis],
			['What it says', content_analysis],
		],
		room,
	);
	return `${header}${shortBlock}${longSections}`.slice(0, ANALYSIS_CHAR_CAP);
};

/** The message the thread opens with.
 *
 * The analysis model writes it and stores it on the find, and this renders it
 * VERBATIM. It is precomputed rather than generated on open for the reason the
 * canned version existed: a live turn leaves the customer watching an empty
 * thread for ~47s. Precomputing gets the model's voice without the wait.
 *
 * NOTHING here composes prose. An earlier version assembled the opener from
 * fixed sentences, and every find then greeted the customer identically — which
 * is exactly what the opener exists to avoid. So the fallback below, for a find
 * saved before `chat_opener` existed, prints only text the model already wrote
 * and adds no sentence of its own.
 */
export const buildOpeningLine = (artifact: InspirationReference): string => {
	const written = artifact.content.chat_opener?.trim();
	if (written) return written;

	// Degraded path, for a find saved before the model wrote the opener. Every
	// line below is the model's own stored prose; the only literals are the
	// markdown bullet and the blank lines that separate paragraphs.
	const { topic, hook, payoff, suggestion, learning, why_it_works } =
		artifact.content;
	const lines: string[] = [];
	if (topic?.trim()) lines.push(`**${topic.trim()}**`);
	const lesson = learning?.trim() || hook?.trim();
	if (lesson) lines.push('', lesson);
	const reasons = asReasonList(why_it_works)
		.filter((point) => point.trim())
		.slice(0, 3);
	if (reasons.length > 0) {
		for (const point of reasons) lines.push(`- ${point.trim()}`);
	} else if (payoff?.trim()) {
		lines.push('', payoff.trim());
	}
	if (suggestion?.trim()) lines.push('', suggestion.trim());
	return lines.join('\n').trim();
};

/** Open the conversation about a post the daily job found.
 *
 * The post rides into the thread as a preview card, the same way a shared post
 * does, so the customer sees the post itself rather than a description of it.
 * `preview_post` is what makes the bridge emit a `panel_artifact` message, which
 * the message list renders as a SocialPostCard — in the thread, not a panel.
 */
export const useJobInspirationChat = (artifact: InspirationReference) => {
	const bridgeApi = useBridgeApi();
	return useCallback(() => {
		trackEvent('Job Inspiration Opened', { artifact_id: artifact.id });
		const { topic, source_url, thumbnail_url, platform, found_by_job_for } =
			artifact.content;
		bridgeApi.sendAction({
			type: 'do_create_chat',
			// A derived id, so re-entering a thread does not repeat the opening
			// line. It does NOT route the press: `source_attribution` below is
			// what returns the customer to the thread this find already opened.
			initial_agent_message: {
				id: `job-inspiration-open:${artifact.id}`,
				content: buildOpeningLine(artifact),
			},
			// The find is what the thread is about, so a second press must return
			// to it rather than mint a twin. A find is opened from two surfaces,
			// the home feed and the job panel, and the panel lists 20 of them.
			source_attribution: {
				kind: 'job_inspiration_find',
				artifact_id: artifact.id,
			},
			// The analysis reaches the model invisibly. Putting it in the
			// transcript would make the customer read what the agent already knows.
			system_message: buildJobContext(artifact),
			// A preview, not a linked_item. The post is what the thread is ABOUT
			// to look at, but it is not the customer's own artifact to edit, and
			// ADO-1907 records what linked_item does to a thread: it becomes the
			// title and the agent's default edit target.
			preview_post: {
				artifact_id: artifact.id,
				title: topic || 'A post from a similar business',
				description: found_by_job_for
					? `From ${found_by_job_for}`
					: 'From a similar business',
				source_post: {
					url: source_url || '',
					platform: platform || '',
					thumbnail_url: thumbnail_url || '',
				},
			},
		});
	}, [bridgeApi, artifact]);
};
